import React, { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { BasicTable } from '../../../components/basic-table';
import { useOrgNames } from '../hooks/use-org-names';
import { atcQueryKeys } from '../query-keys';
import { isOrgUuid } from '../utils/org-id';

interface OrgInvocationsTableProps {
  timeInterval: TimeInterval;
  serviceFilter?: string;
}

interface OrgRow {
  org: string;
  organizationName: string;
  successCount: number;
  failureCount: number;
  activeCount: number;
  total: number;
}

interface SystemServiceRow {
  service: string;
  successCount: number;
  failureCount: number;
  total: number;
}

export const OrgInvocationsTable: React.FC<OrgInvocationsTableProps> = ({
  timeInterval,
  serviceFilter,
}) => {
  const client = useUntenantedClient();

  const { data: invocationData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getInvocationSummary({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.invocationSummary(timeInterval),
  });

  const { data: activeData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getActiveInvocationsByOrg({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.activeInvocationsByOrg(),
  });

  const filtered = useMemo(
    () =>
      serviceFilter !== undefined
        ? invocationData.filter((row) => row.service === serviceFilter)
        : invocationData,
    [invocationData, serviceFilter]
  );

  const activeMap = useMemo(
    () => new Map(activeData.map((a) => [a.org, a.activeCount])),
    [activeData]
  );

  // Aggregate by org (invocation summary can have multiple rows per org across services)
  const byOrg = useMemo(() => {
    const map = new Map<
      string,
      { successCount: number; failureCount: number; total: number }
    >();
    for (const row of filtered) {
      if (!isOrgUuid(row.org)) {
        continue;
      }
      const existing = map.get(row.org) ?? {
        successCount: 0,
        failureCount: 0,
        total: 0,
      };
      map.set(row.org, {
        successCount: existing.successCount + row.successCount,
        failureCount: existing.failureCount + row.failureCount,
        total: existing.total + row.total,
      });
    }
    return map;
  }, [filtered]);

  // Invocations not scoped to an org, grouped by service.
  const systemData = useMemo<SystemServiceRow[]>(() => {
    const map = new Map<
      string,
      { successCount: number; failureCount: number; total: number }
    >();
    for (const row of filtered) {
      if (isOrgUuid(row.org)) {
        continue;
      }
      const existing = map.get(row.service) ?? {
        successCount: 0,
        failureCount: 0,
        total: 0,
      };
      map.set(row.service, {
        successCount: existing.successCount + row.successCount,
        failureCount: existing.failureCount + row.failureCount,
        total: existing.total + row.total,
      });
    }
    return [...map.entries()]
      .map(([service, counts]) => ({ service, ...counts }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  const orgIds = useMemo(() => [...byOrg.keys()], [byOrg]);
  const orgMap = useOrgNames(orgIds);

  const data = useMemo<OrgRow[]>(
    () =>
      [...byOrg.entries()].map(([org, counts]) => {
        const activeCount = activeMap.get(org) ?? 0;
        return {
          org,
          organizationName: orgMap.get(org) ?? org,
          ...counts,
          activeCount,
          total: counts.total + activeCount,
        };
      }),
    [byOrg, orgMap, activeMap]
  );

  const columns = useMemo<ColumnDef<OrgRow>[]>(
    () => [
      {
        accessorKey: 'organizationName',
        header: 'Organization',
        cell: (info) => (
          <span className="font-medium text-gray-900">
            {info.getValue() as string}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'successCount',
        header: 'Success',
        cell: (info) => (
          <span className="text-green-600 font-medium">
            {info.getValue() as number}
          </span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'failureCount',
        header: 'Failures',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-red-600">{value}</span>
          ) : (
            <span>{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'activeCount',
        header: 'Active',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-amber-600">{value}</span>
          ) : (
            <span className="text-gray-400">{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: (info) => (
          <span className="font-medium">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  const systemColumns = useMemo<ColumnDef<SystemServiceRow>[]>(
    () => [
      {
        accessorKey: 'service',
        header: 'Service',
        cell: (info) => (
          <span className="font-mono text-xs text-gray-700">
            {info.getValue() as string}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'successCount',
        header: 'Success',
        cell: (info) => (
          <span className="text-green-600 font-medium">
            {info.getValue() as number}
          </span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'failureCount',
        header: 'Failures',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-red-600">{value}</span>
          ) : (
            <span>{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        cell: (info) => (
          <span className="font-medium">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  if (data.length === 0 && systemData.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">
          Invocations by Organization
        </h3>
        <p className="text-sm text-muted-foreground">
          No invocations found in this time range.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {data.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">
            Invocations by Organization
          </h3>
          <BasicTable
            data={data}
            columns={columns}
            enableSorting={true}
            enableFiltering={true}
            enablePagination={true}
            pageSize={20}
          />
        </div>
      )}

      {systemData.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">
            System &amp; Non-Org Jobs
          </h3>
          <p className="text-xs text-gray-500">
            Invocations not scoped to an organization, grouped by service
          </p>
          <BasicTable
            data={systemData}
            columns={systemColumns}
            enableSorting={true}
            enableFiltering={true}
            enablePagination={true}
            pageSize={10}
          />
        </div>
      )}
    </div>
  );
};
